import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import {
    selectComparator,
    selectPlaceCategories,
    selectSizeBuckets,
    selectStore
} from "modules/customer/insights/portfolioNew/portfolioSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";
import { RagIndicator, RagIndicatorStatus } from "domain/ragIndicator";

import { loadNearbyStores, NearbyStore } from "./nearbyStore";
import _ from "lodash";
import { max, median, min, mode, round } from "mathjs";
import { numberSortExpression, SortDirection, stringSortExpression } from "utils/sortUtils";
import { SizeBucket } from "modules/customer/insights/portfolioNew/sizeBucket";
import { PlaceSuperCategory } from "./placeSuperCategory";
import { ChainStatus, ChainStatusName } from "./chainStatus";
import { StoreSize } from "./storeSize";
import mathUtils from "utils/mathUtils";

interface AreaHealthState {
    isLoading: boolean,
    hasErrors: boolean,
    retailZoneInfoIsVisible: boolean,
    retailMixSuperCategories: PlaceSuperCategory[],
    nearbyStores: NearbyStore[],
    chainStatus: ChainStatus[],
    storeSizes: StoreSize[],
    showUnclassifiedCategoryStores: boolean
}

interface LoadAreaHealthResponse {
    retailMixSuperCategories: PlaceSuperCategory[],
    nearbyStores: NearbyStore[],
    chainStatus: ChainStatus[],
    storeSizes: StoreSize[]
}

const initialState: AreaHealthState = {
    isLoading: false,
    hasErrors: false,
    retailZoneInfoIsVisible: false,
    retailMixSuperCategories: [],
    nearbyStores: [],
    chainStatus: [],
    storeSizes: [],
    showUnclassifiedCategoryStores: false
};

const areaHealthSlice = createSlice({
    name: "customer/insights/portfolioNew/areaHealthNew",
    initialState,
    reducers: {
        showRetailZoneInfo: (state) => {
            state.retailZoneInfoIsVisible = true;
        },
        hideRetailZoneInfo: (state) => {
            state.retailZoneInfoIsVisible = false;
        },
        resetRetailZoneInfoIsVisible: (state) => {
            state.retailZoneInfoIsVisible = initialState.retailZoneInfoIsVisible;
        },
        clearRetailMixSuperCategories: (state) => {
            state.retailMixSuperCategories = initialState.retailMixSuperCategories;
        },
        clearNearbyStores: (state) => {
            state.nearbyStores = initialState.nearbyStores;
        },
        clearChainStatus: (state) => {
            state.chainStatus = initialState.chainStatus;
        },
        clearStoreSizes: (state) => {
            state.storeSizes = initialState.storeSizes;
        },
        clearShowUnclassifiedCategoryStores: (state) => {
            state.showUnclassifiedCategoryStores = initialState.showUnclassifiedCategoryStores;
        },
        chooseAllRetailMixSuperCategories: (state) => {
            state.retailMixSuperCategories.forEach(category => category.setIsSelected(true));
        },
        chooseAllChainStatus: (state) => {
            state.chainStatus.forEach(status => status.setIsSelected(true));
        },
        chooseAllStoreSizes: (state) => {
            state.storeSizes.forEach(size => size.setIsSelected(true));
        },
        deselectAllRetailMixSuperCategories: (state) => {
            state.retailMixSuperCategories.forEach(category => category.setIsSelected(false));
        },
        deselectAllChainStatus: (state) => {
            state.chainStatus.forEach(category => category.setIsSelected(false));
        },
        deselectAllStoreSizes: (state) => {
            state.storeSizes.forEach(size => size.setIsSelected(false));
        },
        toggleRetailMixSuperCategoryIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.retailMixSuperCategories.find(category => category.id === id)?.toggleIsSelected();
        },
        toggleChainStatusIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.chainStatus.find(status => status.id === id)?.toggleIsSelected();
        },
        toggleStoreSizeIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.storeSizes.find(size => size.id === id)?.toggleIsSelected();
        },
        toggleShowUnclassifiedCategoryStores: (state) => {
            state.showUnclassifiedCategoryStores = !state.showUnclassifiedCategoryStores;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadAreaHealthNew.pending, (state: AreaHealthState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.retailMixSuperCategories = initialState.retailMixSuperCategories;
            state.nearbyStores = initialState.nearbyStores;
            state.chainStatus = initialState.chainStatus;
            state.storeSizes = initialState.storeSizes;
        });
        builder.addCase(loadAreaHealthNew.rejected, (state: AreaHealthState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.retailMixSuperCategories = initialState.retailMixSuperCategories;
            state.nearbyStores = initialState.nearbyStores;
            state.chainStatus = initialState.chainStatus;
            state.storeSizes = initialState.storeSizes;
        });
        builder.addCase(loadAreaHealthNew.fulfilled, (state: AreaHealthState, action: PayloadAction<LoadAreaHealthResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.retailMixSuperCategories = action.payload.retailMixSuperCategories;
            state.nearbyStores = action.payload.nearbyStores;
            state.chainStatus = action.payload.chainStatus;
            state.storeSizes = action.payload.storeSizes;
        });
    }
});

export const {
    showRetailZoneInfo,
    hideRetailZoneInfo,
    chooseAllRetailMixSuperCategories,
    chooseAllChainStatus,
    chooseAllStoreSizes,
    deselectAllRetailMixSuperCategories,
    deselectAllChainStatus,
    deselectAllStoreSizes,
    toggleRetailMixSuperCategoryIsSelected,
    toggleChainStatusIsSelected,
    toggleStoreSizeIsSelected,
    toggleShowUnclassifiedCategoryStores
} = areaHealthSlice.actions;

export const loadAreaHealthNew = createAppAsyncThunk(
    "customer/insights/portfolioNew/areahealthNew/loadAreaHealth",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const placeCategories = selectPlaceCategories(state).filter(placeCategory => placeCategory.isDestination === true);
            const sizeBuckets = selectSizeBuckets(state);
            const selectedStore = selectStore(state);
            const comparator = selectComparator(state);

            const superCategories = _(placeCategories)
                .filter(category => category.isRetailer)
                .groupBy(category => category.superCategoryName)
                .map((collection, superCategoryName) => new PlaceSuperCategory(
                    superCategoryName,
                    collection.map(item => item.id),
                    true
                ))
                .sort((a, b) => stringSortExpression(a.name, b.name, SortDirection.ASC))
                .value();

            const comparatorStores = comparator?.getStores() ?? [];
            const selectedAndComparatorStores = selectedStore ? [selectedStore, ...comparatorStores] : [];

            const nearbyStores = await thunkAPI.dispatch(loadNearbyStores(selectedAndComparatorStores, placeCategories, sizeBuckets));

            const chainStatus: ChainStatus[] = [];
            const chain = new ChainStatus(
                ChainStatusName.Chain,
                true
            );
            chainStatus.push(chain);
            const independant = new ChainStatus(
                ChainStatusName.Independent,
                true
            );
            chainStatus.push(independant);

            const storeSizes: StoreSize[] = [];
            const unknownStoreSize = new StoreSize(
                "Unknown",
                true
            );
            storeSizes.push(unknownStoreSize);
            const sizeBucketLabels = new Set(sizeBuckets.map(size => size.label));

            sizeBucketLabels.forEach(sizeLabel => {
                storeSizes.push(new StoreSize(
                    sizeLabel,
                    true
                ));
            });

            const loadAreaHealthResponse: LoadAreaHealthResponse = {
                retailMixSuperCategories: superCategories,
                nearbyStores,
                chainStatus,
                storeSizes
            };

            return loadAreaHealthResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading AreaHealth New.", error));
            return thunkAPI.rejectWithValue([]);
        }
    }
);

export const clearAreaHealth = (): AppThunk => (dispatch) => {
    dispatch(areaHealthSlice.actions.clearNearbyStores());
    dispatch(areaHealthSlice.actions.clearRetailMixSuperCategories());
    dispatch(areaHealthSlice.actions.resetRetailZoneInfoIsVisible());
    dispatch(areaHealthSlice.actions.clearChainStatus());
    dispatch(areaHealthSlice.actions.clearStoreSizes());
    dispatch(areaHealthSlice.actions.clearShowUnclassifiedCategoryStores());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.hasErrors;
};

export const selectRetailZoneInfoIsVisible = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.retailZoneInfoIsVisible;
};

export const selectRetailMixSuperCategories = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.retailMixSuperCategories;
};

export const selectNearbyStores = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.nearbyStores;
};

export const selectChainStatus = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.chainStatus;
};

export const selectStoreSizes = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.storeSizes;
};

export const selectShowUnclassifiedCategoryStores = (state: RootState) => {
    return state.customer.insights.portfolioNew.areaHealthNew.showUnclassifiedCategoryStores;
};

export const selectAreAllStoreCategoriesChosen = createSelector(
    selectRetailMixSuperCategories,
    (superCategories) => {
        const categoriesListSize = superCategories.length;
        const selectedCategoriesListSize = superCategories.filter(category => category.isSelected()).length;

        if (categoriesListSize === selectedCategoriesListSize) {
            return true;
        }
        return false;
    }
);

export const selectFilteredNearbyStores = createSelector(
    selectRetailMixSuperCategories,
    selectNearbyStores,
    (superCategories, nearbyStores) => {
        if (nearbyStores.length === 0) {
            return [];
        }

        const selectedPlaceCategoryIds = new Set(superCategories.filter(item => item.isSelected()).flatMap(item => item.placeCategoryIds));
        return nearbyStores.filter(store => selectedPlaceCategoryIds.has(store.placeCategoryId));
    }
);

export const selectNumberOfStores = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectChainStatus,
    selectStoreSizes,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (isLoading, hasErrors, nearbyStores, chainStatus, storeSizes, selectedStore, comparator) => {
        const numberOfStores = {
            selectedStore: { totalStoreCount: 0, chainCount: 0 },
            comparator: { totalStoreCount: 0, chainCount: 0 },
            difference: 0
        };

        if (isLoading || hasErrors || nearbyStores.length === 0 || !selectedStore || !comparator) {
            return numberOfStores;
        }

        const selectedChainStatus = chainStatus.filter(item => item.isSelected()).map(item => item.name);
        if (selectedChainStatus.length === 1) {
            nearbyStores = selectedChainStatus.includes(ChainStatusName.Chain) && !selectedChainStatus.includes(ChainStatusName.Independent)
                ? nearbyStores.filter(store => store.brandName !== null)
                : nearbyStores.filter(store => store.brandName === null);
        }
        else if (selectedChainStatus.length === 0) {
            nearbyStores = [];
        }

        const selectedStoreSizes = storeSizes.filter(spendCategory => spendCategory.isSelected());
        const selectedStoreSizesNames = selectedStoreSizes.map(sizes => sizes.name);
        nearbyStores = nearbyStores.filter(store => selectedStoreSizesNames.includes(store.sizeBucketName));

        const storeCountsByClientStore = _(nearbyStores)
            .groupBy(store => store.clientStoreId)
            .map((group, clientStoreId) => {
                return {
                    storeId: clientStoreId,
                    numberOfStores: group.length,
                    numberOfChains: group.filter(store => !store.isIndependent).length
                };
            })
            .value();
        numberOfStores.selectedStore.totalStoreCount = storeCountsByClientStore.find(item => item.storeId === selectedStore.id)?.numberOfStores ?? 0;
        numberOfStores.selectedStore.chainCount = storeCountsByClientStore.find(item => item.storeId === selectedStore.id)?.numberOfChains ?? 0;

        const comparatorStoreIds = comparator.getStores().map(store => store.id);
        const comparatorValues = storeCountsByClientStore
            .filter(item => comparatorStoreIds.includes(item.storeId));

        const comparatorTotalStoreCounts = comparatorValues.map(item => item.numberOfStores);
        numberOfStores.comparator.totalStoreCount = comparatorTotalStoreCounts.length === 0 ? 0 : median(comparatorTotalStoreCounts);
        const comparatorChainCounts = comparatorValues.map(item => item.numberOfChains);
        numberOfStores.comparator.chainCount = comparatorChainCounts.length === 0 ? 0 : median(comparatorChainCounts);

        const selectedScalingFactor = 100 / numberOfStores.selectedStore.totalStoreCount;
        const selectedScaledChainCount = selectedScalingFactor * numberOfStores.selectedStore.chainCount;
        const comparatorScalingFactor = 100 / numberOfStores.comparator.totalStoreCount;
        const scaledComparatorChainCount = comparatorScalingFactor * numberOfStores.comparator.chainCount;
        numberOfStores.difference = round(selectedScaledChainCount - scaledComparatorChainCount);

        return numberOfStores;
    }
);

export const selectChainIndependantSplit = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectStoreSizes,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (isLoading, hasErrors, nearbyStores, storeSizes, selectedStore, comparator) => {
        const numberOfStores = {
            selectedStore: { totalStoreCount: 0, chainCount: 0 },
            comparator: { totalStoreCount: 0, chainCount: 0 },
            difference: 0
        };

        if (isLoading || hasErrors || nearbyStores.length === 0 || !selectedStore || !comparator) {
            return numberOfStores;
        }

        const selectedStoreSizes = storeSizes.filter(spendCategory => spendCategory.isSelected());
        const selectedStoreSizesNames = selectedStoreSizes.map(sizes => sizes.name);
        nearbyStores = nearbyStores.filter(store => selectedStoreSizesNames.includes(store.sizeBucketName));

        const storeCountsByClientStore = _(nearbyStores)
            .groupBy(store => store.clientStoreId)
            .map((group, clientStoreId) => {
                return {
                    storeId: clientStoreId,
                    numberOfStores: group.length,
                    numberOfChains: group.filter(store => !store.isIndependent).length
                };
            })
            .value();
        numberOfStores.selectedStore.totalStoreCount = storeCountsByClientStore.find(item => item.storeId === selectedStore.id)?.numberOfStores ?? 0;
        numberOfStores.selectedStore.chainCount = storeCountsByClientStore.find(item => item.storeId === selectedStore.id)?.numberOfChains ?? 0;

        const comparatorStoreIds = comparator.getStores().map(store => store.id);
        const comparatorValues = storeCountsByClientStore
            .filter(item => comparatorStoreIds.includes(item.storeId));

        const comparatorTotalStoreCounts = comparatorValues.map(item => item.numberOfStores);
        numberOfStores.comparator.totalStoreCount = comparatorTotalStoreCounts.length === 0 ? 0 : median(comparatorTotalStoreCounts);
        const comparatorChainCounts = comparatorValues.map(item => item.numberOfChains);
        numberOfStores.comparator.chainCount = comparatorChainCounts.length === 0 ? 0 : median(comparatorChainCounts);

        const selectedScalingFactor = 100 / numberOfStores.selectedStore.totalStoreCount;
        const selectedScaledChainCount = selectedScalingFactor * numberOfStores.selectedStore.chainCount;
        const comparatorScalingFactor = 100 / numberOfStores.comparator.totalStoreCount;
        const scaledComparatorChainCount = comparatorScalingFactor * numberOfStores.comparator.chainCount;
        numberOfStores.difference = round(selectedScaledChainCount - scaledComparatorChainCount);

        return numberOfStores;
    }
);

export const selectGroupedSizeBuckets = createSelector(
    (state: RootState) => selectSizeBuckets(state),
    (sizeBuckets) => {
        if (sizeBuckets.length === 0) {
            return [];
        }

        let id = 0;
        const groupedSizeBuckets = _([...sizeBuckets])
            .sort((a, b) => numberSortExpression(a.id, b.id, SortDirection.ASC))
            .groupBy(sizeBucket => sizeBucket.label)
            .map((group, label) => {
                const lowerBound = min(group.map(sizeBucket => sizeBucket.lowerBound));
                const upperBounds = group.map(sizeBucket => sizeBucket.upperBound);
                //@ts-ignore
                const upperBound = upperBounds.includes(null) ? null : max(upperBounds);
                return new SizeBucket(
                    id++,
                    lowerBound,
                    upperBound,
                    label
                );
            })
            .value();

        return groupedSizeBuckets;
    }
);

export const selectMostCommonStoreSize = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectGroupedSizeBuckets,
    selectChainStatus,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (state: RootState) => selectSizeBuckets(state),
    (isLoading, hasErrors, nearbyStores, groupedSizeBuckets, chainStatus, selectedStore, comparator, sizeBuckets) => {
        const mostCommonStoreSize: { selectedStore: SizeBucket | undefined, comparator: SizeBucket | undefined } = {
            selectedStore: undefined,
            comparator: undefined
        };
        if (isLoading || hasErrors || nearbyStores.length === 0 || !selectedStore || !comparator) {
            return mostCommonStoreSize;
        }

        const selectedChainStatus = chainStatus.filter(item => item.isSelected()).map(item => item.name);
        if (selectedChainStatus.length === 1) {
            nearbyStores = selectedChainStatus.includes(ChainStatusName.Chain) && !selectedChainStatus.includes(ChainStatusName.Independent)
                ? nearbyStores.filter(store => store.brandName !== null)
                : nearbyStores.filter(store => store.brandName === null);
        }
        else if (selectedChainStatus.length === 0) {
            nearbyStores = [];
        }

        const groupedSizeBucketsByOriginalSizeBucketId = new Map<number, SizeBucket | undefined>(
            sizeBuckets.map(sizeBucket => {
                return [sizeBucket.id, groupedSizeBuckets.find(groupedSizeBucket => groupedSizeBucket.label === sizeBucket.label)];
            })
        );

        const mostCommonStoreSizeByClientStore = _(nearbyStores)
            .groupBy(store => store.clientStoreId)
            .map((group, clientStoreId) => {
                const groupedSizeBuckets = group.map(item => groupedSizeBucketsByOriginalSizeBucketId.get(item.sizeBucketId)).filter(item => item !== undefined);
                //@ts-ignore
                const modeValues = groupedSizeBuckets.length > 0 ? mode(groupedSizeBuckets.map(item => item.id)) as number[] : [];
                const smallestOfModes = modeValues.length > 0 ? modeValues.sort((a, b) => numberSortExpression(a, b, SortDirection.ASC))[0] : null;
                return {
                    storeId: clientStoreId,
                    mostCommonStoreSizeId: smallestOfModes
                };
            })
            .value();

        const selectedStoreStoreSizeId = mostCommonStoreSizeByClientStore.find(item => item.storeId === selectedStore.id)?.mostCommonStoreSizeId ?? 0;
        mostCommonStoreSize.selectedStore = groupedSizeBuckets.find(item => item.id === selectedStoreStoreSizeId);

        const comparatorStoreIds = comparator.getStores().map(store => store.id);
        //@ts-ignore
        const comparatorValues: number[] = mostCommonStoreSizeByClientStore
            .filter(item => comparatorStoreIds.includes(item.storeId) && item.mostCommonStoreSizeId !== null)
            .map(item => item.mostCommonStoreSizeId);

        const comparatorMedianStoreSizeId = comparatorValues.length === 0 ? 0 : round(median(comparatorValues));
        mostCommonStoreSize.comparator = groupedSizeBuckets.find(item => item.id === comparatorMedianStoreSizeId);
        return mostCommonStoreSize;
    }
);

export const selectNumberOfStoresPerCategory = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectChainStatus,
    selectStoreSizes,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (state: RootState) => selectPlaceCategories(state),
    (isLoading, hasErrors, nearbyStores, chainStatus, storeSizes, selectedStore, comparator, placeCategories) => {
        if (isLoading || hasErrors || nearbyStores.length === 0 || !selectedStore || !comparator) {
            return [];
        }

        const selectedChainStatus = chainStatus.filter(item => item.isSelected()).map(item => item.name);
        if (selectedChainStatus.length === 1) {
            nearbyStores = selectedChainStatus.includes(ChainStatusName.Chain) && !selectedChainStatus.includes(ChainStatusName.Independent)
                ? nearbyStores.filter(store => store.brandName !== null)
                : nearbyStores.filter(store => store.brandName === null);
        }
        else if (selectedChainStatus.length === 0) {
            nearbyStores = [];
        }

        const selectedStoreSizes = storeSizes.filter(spendCategory => spendCategory.isSelected());
        const selectedStoreSizesNames = selectedStoreSizes.map(sizes => sizes.name);
        nearbyStores = nearbyStores.filter(store => selectedStoreSizesNames.includes(store.sizeBucketName));

        const storeCountsByCategory = _(nearbyStores)
            .groupBy(store => store.placeCategoryId)
            .map((groupByCategory, categoryId) => {
                const countsByStore = _(groupByCategory)
                    .groupBy(store => store.clientStoreId)
                    .map((groupByStore, clientStoreId) => ({
                        clientStoreId,
                        numberOfStores: groupByStore.length
                    }))
                    .value();

                return {
                    categoryId: Number(categoryId),
                    countsByStore
                };
            });

        const allRelevantPlaceCategoryIds = nearbyStores.map(store => store.placeCategoryId);
        const uniquePlaceCategoryIds = Array.from(new Set(allRelevantPlaceCategoryIds));
        const relevantPlaceCategories = placeCategories.filter(item => uniquePlaceCategoryIds.includes(item.id));

        const comparatorStores = comparator.getStores();
        const comparatorStoreIds = comparatorStores.map(store => store.id);

        const storeCountsAllCats = relevantPlaceCategories.map(category => {
            const storeCounts = {
                category,
                selectedStore: 0,
                comparator: 0
            };
            const counts = storeCountsByCategory.find(count => count.categoryId === category.id)?.countsByStore;
            if (!counts) {
                return storeCounts;
            }
            storeCounts.selectedStore = counts.find(item => item.clientStoreId === selectedStore.id)?.numberOfStores ?? 0;

            const newComparatorCounts = comparatorStoreIds.map(clientStoreId => {
                const storeCount = counts.find(count => count.clientStoreId === clientStoreId);
                return storeCount?.numberOfStores ?? 0;
            });

            const comparatorMedian = newComparatorCounts.length === 0 ? 0 : median(newComparatorCounts);
            storeCounts.comparator = comparatorMedian;

            return storeCounts;
        });

        const storeCountsBySuperCategories = _(storeCountsAllCats)
            .groupBy(item => item.category.superCategoryName)
            .map((group, placeSuperCategory) => {
                return {
                    placeSuperCategory,
                    selectedStoreTotalStoreCount: group.reduce((total, current) => total + current.selectedStore, 0),
                    comparatorTotalStoreCount: group.reduce((total, current) => total + current.comparator, 0),
                    storeCountByPlaceCategory: group.map(item => ({
                        placeCategory: item.category,
                        selectedStoreStoreCount: item.selectedStore,
                        comparatorStoreCount: item.comparator
                    })).sort((a, b) => numberSortExpression(a.selectedStoreStoreCount, b.selectedStoreStoreCount, SortDirection.DESC))
                };
            })
            .value()
            .sort((a, b) => numberSortExpression(a.selectedStoreTotalStoreCount, b.selectedStoreTotalStoreCount, SortDirection.DESC));
        return storeCountsBySuperCategories;
    }
);

export enum RetailComplexionLevel {
    BrandClassification = "Brand Classification",
    BrandName = "Brand Name"
}

export interface RetailComplexionTreemapRow {
    name: string,
    type: RetailComplexionLevel,
    colourId: number,
    parent: string | undefined,
    numberOfStores: number,
    percentageOfLevel: number
}

export const selectRetailComplexion = createSelector(
    (state: RootState) => selectStore(state),
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectChainStatus,
    selectStoreSizes,
    selectShowUnclassifiedCategoryStores,
    (selectedStore, isLoading, hasErrors, nearbyStores, chainStatus, storeSizes, showUnclassifiedCategoryStores) => {
        if (!selectedStore || isLoading || hasErrors || nearbyStores.length === 0) {
            return [];
        }

        // Only looking at selected store for now
        let filteredStores = nearbyStores.filter(store => store.clientStoreId === selectedStore.id);

        const selectedChainStatus = chainStatus.filter(item => item.isSelected()).map(item => item.name);
        if (selectedChainStatus.length === 1) {
            filteredStores = selectedChainStatus.includes(ChainStatusName.Chain) && !selectedChainStatus.includes(ChainStatusName.Independent)
                ? filteredStores.filter(store => store.brandName !== null)
                : filteredStores.filter(store => store.brandName === null);
        }
        else if (selectedChainStatus.length === 0) {
            filteredStores = [];
        }

        const selectedStoreSizes = storeSizes.filter(spendCategory => spendCategory.isSelected());
        const selectedStoreSizesNames = selectedStoreSizes.map(sizes => sizes.name);
        filteredStores = filteredStores.filter(store => selectedStoreSizesNames.includes(store.sizeBucketName));
        if (showUnclassifiedCategoryStores === false) {
            filteredStores = filteredStores.filter(
                store => store.brandClassification.name !== "Unclassified"
            );
        }


        const totalNumberofStores = filteredStores.length;
        const brandClassifications = _(filteredStores)
            .groupBy(item => item.brandClassification.name)
            .map((group, classificationName) => {
                const numberOfStores = group.length;
                const aggregatedBrandClassifications: RetailComplexionTreemapRow = {
                    name: classificationName,
                    colourId: group[0].brandClassification.colourId,
                    type: RetailComplexionLevel.BrandClassification,
                    parent: undefined,
                    numberOfStores,
                    percentageOfLevel: 100 * (numberOfStores / totalNumberofStores)
                };
                return aggregatedBrandClassifications;
            }).value();

        const brandNames = _(filteredStores)
            .groupBy(item => item.brandName)
            .map((group, name) => {
                const numberOfStores = group.length;
                const parentName = group[0].brandClassification.name;
                const numberOfStoresInParent = brandClassifications.find(item => item.name === parentName)?.numberOfStores ?? numberOfStores;
                const aggregatedBrandNames: RetailComplexionTreemapRow = {
                    name: name === "null" ? "Independents" : name,
                    colourId: 0,
                    type: RetailComplexionLevel.BrandName,
                    parent: parentName,
                    numberOfStores,
                    percentageOfLevel: 100 * (numberOfStores / numberOfStoresInParent)
                };
                return aggregatedBrandNames;
            }).value();

        return [...brandClassifications, ...brandNames];
    }
);

export const selectIndependantsRateRAG = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (isLoading, hasErrors, nearbyStores, selectedStore, selectedComparator) => {
        const id = "independents-rate";
        const label = "Independents rate";
        let value = "";
        let status = RagIndicatorStatus.Info;

        if (isLoading || hasErrors || !selectedStore || !selectedComparator) {
            return new RagIndicator(id, status, label, value, isLoading, hasErrors);
        }

        const storeCountsByClientStore = _(nearbyStores)
            .groupBy(store => store.clientStoreId)
            .map((group, clientStoreId) => {
                return {
                    storeId: clientStoreId,
                    stores: group,
                    numberOfStores: group.length
                };
            })
            .value();

        const comparatorStoreIds = selectedComparator.getStores().map(store => store.id);
        const comparatorValues = storeCountsByClientStore
            .filter(item => comparatorStoreIds.includes(item.storeId));
        const comparatorTotalStoreCounts = comparatorValues.map(item => item.numberOfStores);
        const compratorTotalStoreCountsGreaterThan10 = comparatorTotalStoreCounts.filter(x => x >= 10).length;

        if (compratorTotalStoreCountsGreaterThan10 <= 20) {
            status = RagIndicatorStatus.NoData;
            value = "There aren't enough relevant stores in your selected comparator to give a meaningful view on how independents affect revenue per sqft. Your comparator must include at least 20 stores, with 10 or more located within their respective retail zones.";
        }
        else {
            const data = comparatorValues.map((comparator) => {
                const independentStoreCount = comparator.stores.filter(store => store.brandName === null).length;
                const numberOfStores = comparator.stores.length;
                const independentRate = independentStoreCount / numberOfStores;
                const revenuePerSquareFoot = selectedComparator.getStores().find(item => item.id === comparator.storeId)?.revenuePerSquareFoot;

                return {
                    id: comparator.storeId,
                    stores: comparator.stores,
                    numberOfStores: comparator.numberOfStores,
                    independentStoreRate: independentRate,
                    revenuePerSquareFoot: revenuePerSquareFoot ?? 0
                };
            });

            const comparatorIndependentStoreRates = data.map(item => item.independentStoreRate);
            const comparatorRevenuePerSquareFoot = data.map(item => item.revenuePerSquareFoot);

            const pearsonCorrelation = mathUtils.pearsonCorrelationPopulation(comparatorIndependentStoreRates, comparatorRevenuePerSquareFoot);

            if (pearsonCorrelation !== null) {
                const selectedStoreValues = storeCountsByClientStore.find(item => item.storeId === selectedStore.id);
                const selectedStoreIndependentStoreCount = selectedStoreValues?.stores.filter(item => item.brandName === null).length ?? 0;
                const selectedStoreStoreCount = selectedStoreValues?.stores.length ?? 0;
                const selectedStoreIndependentStoreRate = (selectedStoreIndependentStoreCount === 0 || selectedStoreStoreCount === 0) ? 0 : selectedStoreIndependentStoreCount / selectedStoreStoreCount;

                const independentRateVariance = selectedStoreIndependentStoreRate - median(comparatorIndependentStoreRates);

                if (pearsonCorrelation > 0.2 && independentRateVariance > 0.1) {
                    status = RagIndicatorStatus.Green;
                    value = `Your ${selectedStore.name} store has high level of independents relative to your comparator stores, and independents have a positive revenue per sq ft impact across your comparator stores.`;
                } else if (pearsonCorrelation > 0.2 && independentRateVariance <= 0.1 && independentRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Your ${selectedStore.name} store has an average level of independents relative to your comparator stores, and independents have a positive revenue per sq ft impact across your comparator stores.`;
                } else if (pearsonCorrelation > 0.2 && independentRateVariance < -0.1) {
                    status = RagIndicatorStatus.Red;
                    value = `Your ${selectedStore.name} store has low level of independents relative to your comparator stores, and independents have a positive revenue per sq ft impact across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && independentRateVariance > 0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Independents don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && independentRateVariance <= 0.1 && independentRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Independents don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && independentRateVariance < -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Independents don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation > -0.2 && independentRateVariance > 0.1) {
                    status = RagIndicatorStatus.Red;
                    value = `Your ${selectedStore.name} store has high level of independents relative to your comparator stores, but independents have a negative revenue per sq ft impact across your comparator stores.`;
                } else if (pearsonCorrelation > -0.2 && independentRateVariance <= 0.1 && independentRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Your ${selectedStore.name} store has an average level of independents relative to your comparator stores, and independents have a negative revenue per sq ft impact across your comparator stores.`;
                } else {
                    status = RagIndicatorStatus.Green;
                    value = `Your ${selectedStore.name} store has low level of independents relative to your comparator store, but independents have a negative revenue per sq ft impact across your comparator stores.`;
                }
            }
        }

        return new RagIndicator(id, status, label, value);
    }
);

export const selectBudgetAndValueOptionsRAG = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    (state: RootState) => selectStore(state),
    (state: RootState) => selectComparator(state),
    (isLoading, hasErrors, nearbyStores, selectedStore, selectedComparator) => {
        const id = "budget-and-value-options";
        const label = "Budget and value options";
        let value = "";
        let status = RagIndicatorStatus.Info;

        if (isLoading || hasErrors || !selectedStore || !selectedComparator) {
            return new RagIndicator(id, status, label, value, isLoading, hasErrors);
        }

        const storeCountsByClientStore = _(nearbyStores)
            .groupBy(store => store.clientStoreId)
            .map((group, clientStoreId) => {
                return {
                    storeId: clientStoreId,
                    stores: group,
                    numberOfStores: group.length
                };
            })
            .value();

        const comparatorStoreIds = selectedComparator.getStores().map(store => store.id);
        const comparatorValues = storeCountsByClientStore
            .filter(item => comparatorStoreIds.includes(item.storeId));
        const comparatorTotalStoreCounts = comparatorValues.map(item => item.numberOfStores);
        const compratorTotalStoreCountsGreaterThan10 = comparatorTotalStoreCounts.filter(x => x >= 10).length;

        if (compratorTotalStoreCountsGreaterThan10 <= 20) {
            status = RagIndicatorStatus.NoData;
            value = "There aren't enough relevant stores in your selected comparator to give a meaningful view on how budget and value options affect revenue per sqft. Your comparator must include at least 20 stores, with 10 or more located within their respective retail zones.";
        }
        else {
            const data = comparatorValues.map((comparator) => {
                const filteredStores = comparator.stores.filter(
                    store => store.brandClassification
                        && store.brandClassification.name !== "Unclassified"
                );
                const budgetStoreCount = filteredStores.filter(
                    store => store.brandClassification.name === "Budget"
                        || store.brandClassification.name === "Value"
                ).length;
                const numberOfStores = filteredStores.length;
                const budgetStoreRate = budgetStoreCount / numberOfStores;
                const revenuePerSquareFoot = selectedComparator.getStores().find(item => item.id === comparator.storeId)?.revenuePerSquareFoot;

                return {
                    id: comparator.storeId,
                    stores: filteredStores,
                    numberOfStores: numberOfStores,
                    budgetStoreRate: budgetStoreRate,
                    revenuePerSquareFoot: revenuePerSquareFoot ?? 0
                };
            });

            const comparatorBudgetStoreRates = data.map(item => item.budgetStoreRate);
            const comparatorRevenuePerSquareFoot = data.map(item => item.revenuePerSquareFoot);

            const pearsonCorrelation = mathUtils.pearsonCorrelationPopulation(comparatorBudgetStoreRates, comparatorRevenuePerSquareFoot);

            if (pearsonCorrelation !== null) {
                const selectedStoreValues = storeCountsByClientStore.find(item => item.storeId === selectedStore.id);
                const selectedStoreClassifiedStores = selectedStoreValues?.stores.filter(
                    item => item.brandClassification
                        && item.brandClassification.name !== "Unclassified"
                );
                const selectedStoreBudgetStoresCount = selectedStoreClassifiedStores?.filter(item =>
                    item.brandClassification.name === "Budget" ||
                    item.brandClassification.name === "Value"
                ).length;
                const selectedStoreBudgetStoreRate = (!selectedStoreClassifiedStores || selectedStoreClassifiedStores.length === 0 || !selectedStoreBudgetStoresCount || selectedStoreBudgetStoresCount === 0) ? 0 : selectedStoreBudgetStoresCount / selectedStoreClassifiedStores.length;

                const budgetRateVariance = selectedStoreBudgetStoreRate - median(comparatorBudgetStoreRates);

                if (pearsonCorrelation > 0.2 && budgetRateVariance > 0.1) {
                    status = RagIndicatorStatus.Green;
                    value = `Your ${selectedStore.name} store has high level of budget and value stores in its retail zone relative to your comparator stores, and these stores have a positive impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation > 0.2 && budgetRateVariance <= 0.1 && budgetRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Your ${selectedStore.name} store has an average level of budget and value stores in its retail zone relative to your comparator stores, and these stores have a positive impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation > 0.2 && budgetRateVariance < -0.1) {
                    status = RagIndicatorStatus.Red;
                    value = `Your ${selectedStore.name} store has low level of budget and value stores in its retail zone relative to your comparator stores, but budget and value stores have a positive impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && budgetRateVariance > 0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Budget and value stores don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && budgetRateVariance <= 0.1 && budgetRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Budget and value stores don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation <= 0.2 && pearsonCorrelation >= -0.2 && budgetRateVariance < -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Budget and value stores don't have a significant impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation < -0.2 && budgetRateVariance > 0.1) {
                    status = RagIndicatorStatus.Red;
                    value = `Your ${selectedStore.name} store has high level of budget and value stores in its retail zone relative to your comparator stores, and these stores have a negative impact on revenue per sq ft across your comparator stores.`;
                } else if (pearsonCorrelation < -0.2 && budgetRateVariance <= 0.1 && budgetRateVariance >= -0.1) {
                    status = RagIndicatorStatus.Amber;
                    value = `Your ${selectedStore.name} store has an average level of budget and value stores in its retail zone relative to your comparator stores, and these stores have a negative impact on revenue per sq ft across your comparator stores.`;
                } else {
                    status = RagIndicatorStatus.Green;
                    value = `Your ${selectedStore.name} store has low level of budget and value stores in its retail zone relative to your comparator stores, but these stores have a negative impact revenue per sq ft across your comparator stores.`;
                }
            }
        }

        return new RagIndicator(id, status, label, value);
    }
);

export default areaHealthSlice;
